/* Revenue — billing dashboard. ARR primary, MRR secondary.
   Annual billing is the source of truth (per api/license/index.js).
   MRR = ARR / 12 for display purposes. */
import React from "react";
import { P, FC } from "../../shared/constants";
import KpiCard from "../components/KpiCard";
import DataTable from "../components/DataTable";
import Chip from "../components/Chip";
var E = React.createElement;

/* Annual prices — keep in sync with api/license/index.js TIERS pricePerYear. */
var ANNUAL_PRICE = { free:0, starter:499, standard:1499, enterprise:4999 };
var TIER_LIST = ["free","starter","standard","enterprise"];

/* Defensive numeric coercion for reducers. Production data has surfaced
   licenses where totalPagesConverted was a string ("docmaya", from a
   corrupt write during an earlier session); 0 + "docmaya" promotes the
   reducer to string concatenation and the KPI renders "00docmayadocmaya…".
   asNum() short-circuits non-numeric inputs to 0 regardless of what the
   API hands back. */
function asNum(v){
  if (typeof v === "number" && isFinite(v)) return v;
  var n = Number(v);
  return isFinite(n) ? n : 0;
}

export default function Revenue(props){
  var licenses = props.licenses || [];

  var arr = licenses.reduce(function(s,l){ return s + asNum(ANNUAL_PRICE[l.tier]); }, 0);
  var mrr = arr / 12;
  var active = licenses.filter(function(l){return l.status==="active"}).length;
  var pagesConverted = licenses.reduce(function(s,l){ return s + asNum(l.totalPagesConverted); }, 0);

  var rows = licenses.map(function(l){ return l; });
  var columns = [
    { key:"domain", label:"Domain", render:function(l){
        return E("div",null,
          E("div",{style:{fontWeight:700}}, l.orgName||l.domain),
          E("div",{style:{fontSize:11,color:P.slateM,fontFamily:FC}}, l.domain)); } },
    { key:"tier", label:"Tier", render:function(l){
        return E(Chip,{tier:l.tier,size:"sm"}, (l.tier||"?").toUpperCase()); } },
    { key:"status", label:"Status", render:function(l){
        return E(Chip,{variant:l.status==="active"?"emerald":"coral",size:"sm"}, l.status||"?"); } },
    { key:"pages", label:"Pages converted", align:"right", render:function(l){
        return asNum(l.totalPagesConverted).toLocaleString(); } },
    { key:"scans", label:"Scans used", align:"right", render:function(l){
        return asNum(l.scansUsedThisYear)+" / "+asNum(l.scansPerYear); } },
    { key:"price", label:"Annual price", align:"right", render:function(l){
        return "$"+asNum(ANNUAL_PRICE[l.tier]).toLocaleString(); } }
  ];

  return E("div",{style:{display:"flex",flexDirection:"column",gap:20}},
    E("section",{"aria-labelledby":"rev-kpi-h"},
      E("h2",{id:"rev-kpi-h",style:{position:"absolute",width:1,height:1,padding:0,margin:-1,overflow:"hidden",clip:"rect(0,0,0,0)",border:0}},"Revenue metrics"),
      E("div",{style:{display:"grid",gap:12,gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))"}},
        E(KpiCard,{label:"ARR estimate", value:"$"+arr.toLocaleString(),
          color:P.emeraldDk||P.emerald, bg:P.emeraldLt,
          sublabel:"$"+Math.round(mrr).toLocaleString()+"/mo equivalent"}),
        E(KpiCard,{label:"Active licenses", value:active,
          color:P.priDk||P.pri, bg:P.priLt}),
        E(KpiCard,{label:"Total pages converted", value:pagesConverted.toLocaleString(),
          color:P.amberDk, bg:P.amberLt}))),

    E("section",{"aria-labelledby":"rev-tiers-h"},
      E("h2",{id:"rev-tiers-h",style:{fontSize:14,fontWeight:800,margin:"0 0 10px"}},"License tier distribution"),
      E("div",{style:{display:"flex",gap:10,flexWrap:"wrap"}},
        TIER_LIST.map(function(t){
          var n = licenses.filter(function(l){return l.tier===t}).length;
          return E("div",{key:t,style:{display:"flex",alignItems:"center",gap:10,
            padding:"10px 14px",borderRadius:10,border:"1px solid "+P.bdr,background:P.card,minWidth:140}},
            E("div",{style:{fontSize:22,fontWeight:900,color:P.slate}}, n),
            E("div",null,
              E("div",{style:{fontSize:11,fontWeight:700,color:P.slate}}, t.charAt(0).toUpperCase()+t.slice(1)),
              E("div",{style:{fontSize:10,color:P.slateM}}, "$"+(ANNUAL_PRICE[t]||0).toLocaleString()+"/yr")));
        }))),

    E("section",{"aria-labelledby":"rev-table-h"},
      E("h2",{id:"rev-table-h",style:{fontSize:14,fontWeight:800,margin:"0 0 10px"}},"Per-license revenue"),
      E(DataTable,{columns:columns,rows:rows,rowKey:function(l){return l.id||l.domain},
        emptyText:"No licenses to report on yet."}))
  );
}
